import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { Head } from '@zg-rentals/next/app';

import { useFeedListingIdRedirect } from '../../../../hooks';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';
import propertyUtils from '../../../../utils/propertyUtils';

import { ERROR_PAGE_TYPES } from '../../../../constants/ErrorMessageConstants';

import { useAuth } from '../../../../components/AuthProvider';
import { CLPLoadingState } from '../../../../components/CreateListingPage/LoadingState';
import ErrorPage from '../../../../components/ErrorPage';
import AuthCheckWrapper from '../../../../containers/AuthCheckWrapper';
import CreateListingPage from '../../../../containers/CreateListingPage';
import PropertyDetailsContainer from '../../../../containers/PropertyDetailsContainer';

import { type DatarouterError } from '@zg-rentals/rental-manager-env';

const PropertyDetailsPage = () => {
  const router = useRouter();
  const listingAlias = router.isReady ? (router.query.listingAlias as string) : '';
  const { isAuthorized, user } = useAuth();

  useFeedListingIdRedirect(listingAlias);

  const {
    data: listingDetails,
    error,
    isPending,
  } = useGetListingDetails(listingAlias, {
    enabled: isAuthorized,
  });

  const canAccessModularListing = accessUtils.canAccessModularListing({
    isMultifamily: listingDetails?.isMultifamily ?? false,
    listingTypeCode: listingDetails?.listingType?.code ?? 0,
    lastActivatedMs: listingDetails?.lastActivatedMs,
    userCanAccessModularListing: user?.features?.canAccessModularListing ?? false,
  });

  // SF Redirect Guard
  useEffect(() => {
    if (user && listingDetails && canAccessModularListing) {
      {
        window.location.href = `/rental-manager/properties/${listingAlias}/create-listing`;
      }
    }
  }, [listingDetails, listingAlias, user, canAccessModularListing]);

  const isInReactivationFlow = accessUtils.canAccessMLReactivationFlow({
    lastActivatedMs: listingDetails?.lastActivatedMs,
    isFeed: listingDetails?.isFeed,
    isMultifamily: listingDetails?.isMultifamily,
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  // Error states
  if (error) {
    const httpStatus = (error as DatarouterError).responseStatus;

    if (httpStatus === 400) {
      return <ErrorPage type={ERROR_PAGE_TYPES.NOT_FOUND} />;
    }

    if (httpStatus === 403) {
      return <ErrorPage type={ERROR_PAGE_TYPES.UNAUTHORIZED} />;
    }

    return <ErrorPage type={ERROR_PAGE_TYPES.TECHNICAL_DIFFICULTIES} />;
  }

  const { formattedStreet } = propertyUtils.formatAddress({
    street: listingDetails?.street,
  });

  if (isPending) {
    return <CLPLoadingState />;
  }

  return (
    <AuthCheckWrapper loading={<CLPLoadingState />} redirecting={<CLPLoadingState />}>
      <Head applicationName="zillow-rental-manager-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
        <title>Edit {formattedStreet || 'listing'} | Zillow Rental Manager</title>
      </Head>
      {!isPending &&
        !canAccessModularListing &&
        (isInReactivationFlow ? <CreateListingPage /> : <PropertyDetailsContainer />)}
    </AuthCheckWrapper>
  );
};

export default PropertyDetailsPage;
